import React from "react";
import { PropTypes } from "prop-types";
import styled from "styled-components";
import { faTrashAlt, faGripVertical, faPencil } from "pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQueryCache } from "react-query";
import axios from "axios";
import Loader from "./Loader";
import ENV from "../constants/envConstants";
import Swal from "sweetalert2";
import { trackEventSegment } from "../utils/segmentUtils";
import { up } from "styled-breakpoints";
import findIndex from "lodash/findIndex";
import MenuItemForm from "./MenuItemForm";

const DishWrap = styled.div`
  display: flex;
  border: 2px #e5e5e5 solid;
  border-radius: 10px;
  width: 100%;
  margin-bottom: 2em;
  padding: 2em;
  box-sizing: border-box;
`;

const DishTitle = styled.span`
  font-weight: 500;
  font-size: 1.1em;
  align-self: center;
  color: #626262;
`;

const DishContent = styled.div`
  display: flex;
  flex: 1;
`;

const DishButtons = styled.div`
  display: flex;
  align-self: center;
  width: 25%;
  justify-content: space-between;

  ${up("xs")} {
    width: 25%;
  }

  ${up("sm")} {
    width: 20%;
  }

  ${up("md")} {
    width: 18%;
  }

  ${up("lg")} {
    width: 16%;
  }

  ${up("xl")} {
    width: 14%;
  }

  ${up("xxl")} {
    width: 11%;
  }

  ${up("giant")} {
    width: 9%;
  }
`;

const removeDish = (data) => {
  const { dishId } = data;
  return axios.delete(ENV.BACKEND_URL + "/restaurants/dishes/" + dishId + "/");
};

const DishItemText = (props) => {
  const cache = useQueryCache();

  const onMutate = (data) => {
    const dishId = data.dishId;
    cache.cancelQueries(["placeDetail"]);
    const previousPlace = cache.getQueryData(["placeDetail"]);

    const newPlace = Object.assign({}, previousPlace);
    const menuIndex = findIndex(newPlace.menus, ["uuid", props.menuId]);
    const sectIndex = findIndex(newPlace.menus[menuIndex].menu_categories, ["uuid", props.sectionId]);
    const dishIndex = findIndex(newPlace.menus[menuIndex].menu_categories[sectIndex].dishes, ["uuid", dishId]);
    newPlace.menus[menuIndex].menu_categories[sectIndex].dishes.splice(dishIndex, 1);
    cache.setQueryData(["placeDetail"], newPlace);
    return () => cache.setQueryData("placeDetail", previousPlace);
  };

  const [mutate] = useMutation(removeDish, { onMutate });

  const clickDelete = () => {
    Swal.fire({
      title: "Are you sure?",
      icon: "warning",
      showCancelButton: true,
      text: "Deleting a dish is an irreversable action.",
      confirmButtonText: "Yes, delete it!",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      showLoaderOnConfirm: true,
      preConfirm: async (result) => {
        await mutate(
          { dishId: props.dish.uuid },
          {
            onError: (resError) => {
              props.addDish(props.dish.uuid);
            },
            onSuccess: () => {
              trackEventSegment("Removed Dish", {
                uuid: props.dish.uuid,
                name: props.dish.name,
              });
            },
          }
        );
      },
      allowOutsideClick: () => !Swal.isLoading(),
    });
  };

  if (props.dish) {
    return (
      <DishWrap>
        <DishContent>
          <DishTitle>
            {props.dish.name} - ${props.dish.price}
          </DishTitle>
        </DishContent>
        <DishButtons>
          <MenuItemForm
            dish={props.dish}
            element={<FontAwesomeIcon color={"#919191"} size="lg" icon={faPencil} />}
            numOfDishes={props.numDishes}
            menuId={props.menuId}
            sectionId={props.sectionId}
            hasImageField={false}
          />
          <FontAwesomeIcon color={"#919191"} size="lg" icon={faTrashAlt} onClick={clickDelete} />
          <FontAwesomeIcon color={"#919191"} size="lg" icon={faGripVertical} />
        </DishButtons>
      </DishWrap>
    );
  }
  return <Loader />;
};

DishItemText.propTypes = {
  dish: PropTypes.object.isRequired,
  menuId: PropTypes.string.isRequired,
  sectionId: PropTypes.string.isRequired,
  numDishes: PropTypes.number,
};

export default DishItemText;
